import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, Subject, switchMap } from 'rxjs';
import { IncidentService } from '../../../core/services/incident.service';
import { SelectComponent, SelectOption } from '../select/select';

/**
 * Person picker backed by server search and paging. `GET /api/persons` always paginates
 * (default 10), so a preloaded list would silently drop people.
 */
@Component({
  selector: 'app-person-select',
  imports: [SelectComponent],
  templateUrl: './person-select.html',
})
export class PersonSelectComponent implements OnInit {
  value = input<string>('');
  /** Label for `value` when it is not in the loaded page (e.g. the saved person). */
  valueLabel = input<string | null>(null);
  placeholder = input<string>('Select person…');
  error = input<boolean>(false);
  changed = output<string>();

  private incidentService = inject(IncidentService);
  private destroyRef = inject(DestroyRef);

  private readonly PAGE_SIZE = 20;
  private pageIndex = 0;
  private term = '';
  private search$ = new Subject<string>();

  private loaded = signal<SelectOption[]>([]);
  private total = signal(0);
  protected loading = signal(false);
  protected hasMore = computed(() => this.loaded().length < this.total());

  /** Names of every person loaded so far: the selected one may drop out of the current page
   * (e.g. picked from a search, then the search is reset on reopen). */
  private knownLabels = new Map<string, string>();

  protected options = computed(() => {
    const list = this.loaded();
    const id = this.value();
    if (!id || list.some((o) => o.value === id)) return list;
    return [{ value: id, label: this.valueLabel() ?? this.knownLabels.get(id) ?? id }, ...list];
  });

  ngOnInit(): void {
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.term = term;
          this.pageIndex = 0;
          this.loading.set(true);
          return this.fetch(0);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.loaded.set(this.toOptions(res.persons));
          this.total.set(res.total);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });

    this.search$.next('');
  }

  protected onSearch(term: string): void {
    this.search$.next(term.trim());
  }

  protected onLoadMore(): void {
    if (this.loading() || !this.hasMore()) return;
    const next = this.pageIndex + 1;
    this.loading.set(true);
    this.fetch(next)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.pageIndex = next;
          this.loaded.update((prev) => [...prev, ...this.toOptions(res.persons)]);
          this.total.set(res.total);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  private fetch(pageIndex: number) {
    return this.incidentService.getPersons(
      this.term || undefined,
      this.PAGE_SIZE,
      undefined,
      undefined,
      pageIndex,
    );
  }

  private toOptions(
    persons: { id: string; firstName: string; surnames: string }[],
  ): SelectOption[] {
    const options = persons.map((p) => ({ value: p.id, label: `${p.firstName} ${p.surnames}` }));
    for (const o of options) this.knownLabels.set(o.value, o.label);
    return options;
  }
}
